'use client';

import { cn } from '@vimond/theme/tailwind';
import { VariantProps, cva } from 'class-variance-authority';
import dynamic from 'next/dynamic';
import { ComponentProps, useState } from 'react';

const InformationCircleIcon = dynamic(() =>
  import('@heroicons/react/24/outline').then(
    (mod) => mod.InformationCircleIcon,
  ),
);
const PlayIcon = dynamic(() =>
  import('@heroicons/react/24/solid').then((mod) => mod.PlayIcon),
);
const ArrowPathIcon = dynamic(() =>
  import('@heroicons/react/24/outline').then((mod) => mod.ArrowPathIcon),
);
const ExclamationCircleIcon = dynamic(() =>
  import('@heroicons/react/24/outline').then(
    (mod) => mod.ExclamationCircleIcon,
  ),
);
const MagnifyingGlassIcon = dynamic(() =>
  import('@heroicons/react/24/outline').then((mod) => mod.MagnifyingGlassIcon),
);
const MinusCircleIcon = dynamic(() =>
  import('@heroicons/react/24/outline').then((mod) => mod.MinusCircleIcon),
);
const ShieldCheckIcon = dynamic(() =>
  import('@heroicons/react/24/outline').then((mod) => mod.ShieldCheckIcon),
);
const LockClosedIcon = dynamic(() =>
  import('@heroicons/react/24/outline').then((mod) => mod.LockClosedIcon),
);

const buttonStyles = cva(
  ['transition', 'disabled:opacity-50', 'inline-block'],
  {
    variants: {
      variant: {
        primary: [
          'font-semibold',
          'text-button-primary-text',
          'bg-button-primary-background',
          'border-button-primary-border',
          'border',
          'rounded-md',
          'hover:bg-button-primary-hover-background/90',
          'hover:text-button-primary-hover-text',
          'ring-2 ring-transparent',
          'hover:ring-button-primary-background/70',
          'focus:ring-button-primary-background/70',
        ],
        secondary: [
          'font-medium',
          'text-button-secondary-text',
          'bg-button-secondary-background',
          'border-button-secondary-border',
          'border',
          'rounded-md',
          'hover:bg-button-secondary-hover-background',
          'hover:text-button-secondary-hover-text',
          'ring-2',
          'hover:ring-button-secondary-background/70',
          'focus:ring-button-secondary-background/70',
        ],
        tertiary: [
          'font-medium',
          'text-button-tertiary-text',
          'bg-button-tertiary-background',
          'border-button-tertiary-border',
          'border',
          'rounded-md',
          'hover:bg-button-tertiary-hover-background',
          'hover:text-button-tertiary-hover-text',
          'focus:ring-4',
          'focus:ring-button-tertiary-background',
        ],
        destructive: [
          'font-medium',
          'text-button-destructive-text',
          'bg-button-destructive-background',
          'border-button-destructive-border',
          'border',
          'rounded-md',
          'hover:bg-button-destructive-hover-background',
          'hover:text-button-destructive-hover-text',
          'focus:ring-4',
          'focus:ring-button-destructive-background',
          'hover:bg-opacity-80',
        ],
        text: [
          'text-button-primary-text',
          'border',
          'border-transparent',
          'rounded-md',
          'hover:bg-opacity-80',
          'ring-0',
        ],
        textColored: [
          'text-text-highlight',
          'font-bold',
          'border-transparent',
          'hover:opacity-80',
          'hover:text-text-primary',
          'active:opacity-100',
          'active:text-text-highlight',
          'disabled:text-text-highlight',
          'transition-all',
          'uppercase',
          '!p-0',
        ],
        destructiveSmall: [
          'cursor-pointer',
          'hover:text-red-500',
          'p-0',
          'm-0',
          'h-auto',
          'w-auto',
        ],
      },
      size: {
        xs: ['text-xs', 'px-2', 'py-2'],
        sm: ['text-sm', 'px-3', 'py-2'],
        md: ['text-md', 'px-5', 'py-2.5'],
        lg: ['text-lg', 'px-8', 'py-3.5'],
      },
      skeleton: {
        true: [
          'bg-skeleton',
          'hover:bg-skeleton',
          'focus:ring-0',
          'animate-pulse',
          'border-0',
        ],
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonStyles> & {
    icon?:
      | 'play'
      | 'info'
      | 'security'
      | 'delete'
      | 'error'
      | 'search'
      | 'skeleton'
      | 'lock';
    loading?: boolean;
    loadingText?: string;
  };

export const Button = ({
  children,
  className,
  icon,
  loading = false,
  loadingText,
  size,
  skeleton,
  variant,
  ...props
}: ButtonProps) => {
  const [hasClicked, setHasClicked] = useState(false);

  const getIcon = (icon: string) => {
    if (hasClicked || loading) {
      return <ArrowPathIcon className="h-5 w-5 animate-spin" />;
    } else if (icon === 'play') {
      return <PlayIcon className="h-6 w-6" />;
    } else if (icon === 'info') {
      return <InformationCircleIcon className="h-6 w-6" />;
    } else if (icon === 'security') {
      return <ShieldCheckIcon className="h-6 w-6" />;
    } else if (icon === 'delete') {
      return <MinusCircleIcon className="h-6 w-6" />;
    } else if (icon === 'error') {
      return <ExclamationCircleIcon className="h-6 w-6" />;
    } else if (icon === 'search') {
      return <MagnifyingGlassIcon className="h-6 w-6" />;
    } else if (icon === 'skeleton') {
      return <div className="h-6 w-6 opacity-0">-</div>;
    } else if (icon === 'lock') {
      return <LockClosedIcon className="h-6 w-6" />;
    }
    return <></>;
  };

  if (icon) {
    return (
      <button
        className={cn(
          'relative top-[2px]',
          buttonStyles({ variant, size, skeleton }),
          className,
        )}
        onClick={() => setHasClicked(true)}
        {...props}
      >
        <div className="flex items-center justify-center gap-1">
          <span aria-hidden className="relative h-6 w-6">
            {getIcon(icon)}
          </span>
          {children}
        </div>
      </button>
    );
  }

  const LoadingSpinner = () => (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  return (
    <button
      className={cn(buttonStyles({ variant, size, skeleton }), className)}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading ? (
        <span className="flex items-center justify-center">
          <LoadingSpinner />
          {loadingText ?? children}
        </span>
      ) : (
        children
      )}
    </button>
  );
};
